import type { VoxelData, VoxelMaterial } from '@/voxelTypes'
import type { LiquidKind } from './kind'

/**
 * A liquid kind that spans several materials of one voxel type: every voxel of `type` is liquid,
 * and a liquid cell of `color` is written back with the material `materialOf(color)` (undefined:
 * the type's default material). For a page with more than one liquid in one sim (liquid metal and
 * glossy goo, which must displace each other), where `liquidKind(type, { material })` would see
 * only one of them and `liquidKind(type)` would write every moved cell back without its material.
 *
 * The sim knows a cell only by its colour, so the colours of different materials must be distinct
 * (a page's palette is fixed, so it knows them all); pair it with a `densityOf` by colour.
 */
export function liquidKindByColor(
  type: string,
  materialOf: (color: number) => VoxelMaterial | undefined,
  opts: { defaultColor?: number } = {},
): LiquidKind {
  const cache = new Map<number, VoxelData>()
  return Object.freeze({
    id: `${type}:by-color`,
    defaultColor: opts.defaultColor ?? 0xffffff,
    matches: (v: VoxelData | undefined) => v !== undefined && v.type === type,
    voxel: (color: number) => {
      const c = color >>> 0
      let v = cache.get(c)
      if (!v) {
        const material = materialOf(c)
        v = Object.freeze(material ? { type, color: c, material } : { type, color: c }) as VoxelData
        cache.set(c, v)
      }
      return v
    },
  })
}
